import {NgModule} from "@angular/core";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {BrowserModule} from "@angular/platform-browser";
import {HttpClientModule, HttpClientXsrfModule} from "@angular/common/http";
import {RouterModule} from "@angular/router";
import {routes} from "./routing/base.routes";
import {TransitionToolComponent} from "./transition-tool.component";
import {ProjectListComponent} from "./project/project-list.component";
import {UserListComponent} from "./user/users.component";
import {SubstituteUserComponent} from "./user-substitute/substitute-user.component";
import {UserEditComponent} from "./user/user-edit.component";
import {ResumeUserComponent} from "./user-switch/resume-user.component";
import {MdProgressSpinnerModule} from "./misc/progress-spinner";
import {ProjectComponent} from "./project/project.component";
import {SiteListComponent} from "./sites/site-list.component";
import {WorkflowModule} from "./workflow/workflow.module";
import {PaginationModule} from "./pagination/pagination.module";
import {UserSelectionModule} from "./user-select/user-selection.module";
import {AssetSelectionModule} from "./asset-select/asset-selection.module";
import {ErrorModule} from "./errors/error.module";
import {OrderListComponent} from "./order/order-list.component";
import {OrderComponent} from "./order/order.component";
import {SiteComponent} from "./sites/site.component";
import {UploaderWrapperModule} from "./uploader-wrapper/uploader-wrapper.module";
import {DartDealImportModule} from "./dart-deal/dartDealImport.module";
import {DeploymentComponent} from "./deployment/deployment.component";
import {DeploymentListComponent} from "./deployment/deployment-list.component";
import {FileUploadModule} from "ng2-file-upload";
import {AppSettingsModule} from "./app-settings/app-settings.module";
import {BootstrapModalModule} from "./bootstrap-modal/bootstrap-modal.module";
import {DartDealListComponent} from "./dart-deal/dart-deal-list.component";
import {HttpHelpersModule} from "./http/http-helpers.module";
import {AddressModule} from "./address/address.module";
import {ContactModule} from "./contact/contact.module";
import {DatePickerModule} from "./datepicker/datepicker.module";
import {TreeViewModule} from "./tree-view/tree-view.module";
import {ConfirmDialogModule} from "./confirm-dialog/confirm-dialog.module";
import {ProjectCreateDialogComponent} from "./project/project-create-dialog.component";
import {SiteCreateDialogComponent} from "./sites/site-create-dialog.component";
import {OrderCreateDialogComponent} from "./order/order-create-dialog.component";
import {DeploymentCreateDialogComponent} from "./deployment/deployment-create-dialog.component";
import {AppAlertModule} from "./app-alert/app-alert.module";
import {DashboardComponent} from "./dashboard/dashboard.component";
import {DeviceDefectListComponent} from "./device-defect/device-defect-list.component";
import {CreateDefectDialogComponent} from "./device-defect/create-defect-dialog.component";
import {DeviceDefectDetailsComponent} from "./device-defect/device-defect-details.component";
import {DeviceRelocationListComponent} from "./device-relocation/device-relocation-list.component";
import {CreateRelocationDialogComponent} from "./device-relocation/create-relocation-dialog.component";
import {DeviceRelocationDetailsComponent} from "./device-relocation/device-relocation-details.component";
import {SandyAssetInfoImportComponent} from "./sandy/sandy-import-page.component";
import {MarkdownModule} from "ngx-markdown";
import {ReleaseNotesComponent} from "./release-notes/release-notes.component";
import {BulkOrderListComponent} from "./bulk-ordering/bulk-ordering-list.component";
import {CreateBulkOrderDialogComponent} from "./bulk-ordering/create-bulk-order-dialog.component";
import {BulkOrderDetailsComponent} from "./bulk-ordering/bulk-ordering-details.component";
import {FormHelpersModule} from "./form-helpers/form-helpers.module";
import {ManageServiceAccountComponent} from "./user/manage-service-account.component";
import {EditServiceAccountComponent} from "./user/edit-service-account.component";
import {DisplayTokenDialogComponent} from "./user/dialog-display-token.component";
import {ListEditorModule} from "./list-editor/list-editor.module";
import {ListOfTextModule} from "./list-of-text/list-of-text.module";
import {HelpDialogModule} from "./help-dialog/help-dialog.module";
import {UpdateTokenDialogComponent} from "./user/dialog-update-token.component";
import {ProjectAssignListComponent} from "./project/project-assign-list.component";
import {WorkflowTrackingReportComponent} from "./workflow/dialog-workflow-tracking-report.component";
import {TestComponent} from "./test.component";
import {UserStatsComponent} from "./user/user-stats.component";
import {HttpClientSequencerInterceptorProvider} from "./http/http-client-sequencer.interceptor";
import {CatchUnauthorizedInterceptorProvider} from "./http/catch-unauthorized.interceptor";
import {ManagePartnersListComponent} from "./user/manage-partners-list.component";
import {PartnerAccountEditComponent} from "./user/partner-account-edit.component";
import {DisplayRegistrationLinkDialogComponent} from "./user/dialog-partner-display-registration-link.component";
import {LoginModule} from "./login/login.module";
import {PropertyContainerModule} from "./property-container/property-container.module";
import {NotificationsModule} from "./notifications/notifications.module";
import {MyTasksComponent} from "./tasks/my-tasks.component";
import {SandyImportComponent} from "./sandy/sandy-import.component";
import {SandyAssociateDialogComponent} from "./sandy/sandy-associate-dialog.component";
import {MigrateComponent} from "./migrate/migrate.component";
import {GraphqlCoreModule} from "./graphql/graphql-core.module";
import {ProjectSyncCdfComponent} from "./project/project-sync-cdf.component";
import {CdfModule} from "./cdf/cdf.module";
import {CronAdminModule} from "./cron-admin/cron-admin.module";
import {BuildingComponent} from "./buildings/building.component";
import {CardExpensesComponent} from "./accounting/card-expenses.component";
import {ImportCARDAccountsDialogComponent} from "./accounting/import-card-accounts-dialog.component";
import {CreateExpenseDialogComponent} from "./accounting/create-expense-dialog.component";
import {BreadcrumbModule} from "./breadcrumbs/breadcrumb.module";
import {SoftwareSolutionsListComponent} from "./software-solutions/software-solutions-list.component";
import {SoftwareSolutionsModule} from "./software-solutions/software-solutions.module";
import {CustomerCustomHeadersDialogComponent} from "./workflow/project/select-target-customer-custom-header-edit-dialog.component";
import {SmartsheetModule} from "./smartsheet/smartsheet.module";

const XSRF_OPTIONS = {
  cookieName: "XSRF-TOKEN",
  headerName: "x-xsrf-token",
};

const ROUTE_OPTIONS = {
  useHash: true,
};

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions(XSRF_OPTIONS),
    FormsModule,
    ReactiveFormsModule,
    HttpHelpersModule,
    MdProgressSpinnerModule,
    WorkflowModule,
    PaginationModule,
    UserSelectionModule,
    AssetSelectionModule,
    ErrorModule,
    AddressModule,
    ContactModule,
    UploaderWrapperModule,
    DartDealImportModule,
    BootstrapModalModule,
    FileUploadModule,
    AppSettingsModule,
    DatePickerModule,
    TreeViewModule,
    ConfirmDialogModule,
    AppAlertModule,
    FormHelpersModule,
    RouterModule.forRoot(routes, ROUTE_OPTIONS),
    MarkdownModule.forRoot(),
    ListEditorModule,
    ListOfTextModule,
    HelpDialogModule,
    LoginModule,
    PropertyContainerModule,
    NotificationsModule,
    GraphqlCoreModule,
    CdfModule,
    CronAdminModule,
    BreadcrumbModule,
    SoftwareSolutionsModule,
    SmartsheetModule,
  ],
  providers: [
    CatchUnauthorizedInterceptorProvider,
    HttpClientSequencerInterceptorProvider,
  ],
  declarations: [
    MigrateComponent,
    TransitionToolComponent,
    ProjectListComponent,
    DeviceDefectListComponent,
    CreateDefectDialogComponent,
    DeviceDefectDetailsComponent,
    DeviceRelocationListComponent,
    CreateRelocationDialogComponent,
    DeviceRelocationDetailsComponent,
    ProjectComponent,
    ProjectSyncCdfComponent,
    UserListComponent,
    SubstituteUserComponent,
    UserEditComponent,
    ResumeUserComponent,
    SiteListComponent,
    OrderListComponent,
    OrderComponent,
    SiteComponent,
    DeploymentComponent,
    DeploymentListComponent,
    DartDealListComponent,
    ProjectCreateDialogComponent,
    SiteCreateDialogComponent,
    OrderCreateDialogComponent,
    DeploymentCreateDialogComponent,
    DashboardComponent,
    SandyAssetInfoImportComponent,
    ReleaseNotesComponent,
    ManageServiceAccountComponent,
    ManagePartnersListComponent,
    PartnerAccountEditComponent,
    EditServiceAccountComponent,
    DisplayTokenDialogComponent,
    BulkOrderListComponent,
    CreateBulkOrderDialogComponent,
    UpdateTokenDialogComponent,
    BulkOrderDetailsComponent,
    TestComponent,
    ProjectAssignListComponent,
    WorkflowTrackingReportComponent,
    UserStatsComponent,
    DisplayRegistrationLinkDialogComponent,
    MyTasksComponent,
    SandyImportComponent,
    SandyAssociateDialogComponent,
    SoftwareSolutionsListComponent,
    CardExpensesComponent,
    ImportCARDAccountsDialogComponent,
    CreateExpenseDialogComponent,
    BuildingComponent,
  ],
  bootstrap: [
    TransitionToolComponent,
  ],
  entryComponents: [
    ProjectCreateDialogComponent,
    SiteCreateDialogComponent,
    OrderCreateDialogComponent,
    CreateDefectDialogComponent,
    CreateRelocationDialogComponent,
    DeploymentCreateDialogComponent,
    DisplayTokenDialogComponent,
    CreateBulkOrderDialogComponent,
    UpdateTokenDialogComponent,
    WorkflowTrackingReportComponent,
    DisplayRegistrationLinkDialogComponent,
    SandyAssociateDialogComponent,
    ImportCARDAccountsDialogComponent,
    CreateExpenseDialogComponent,
    CustomerCustomHeadersDialogComponent,
  ],
})

export class TransitionToolModule {
}
